import React, { useId, useState } from 'react';
import { useField } from 'react-final-form';
import { css } from '@linaria/core';
import { styled } from '@linaria/react';

import { ERROR_PASSWORD_EMPTY } from '../../constants';
import { Checkbox, FieldSubmitError, Input, Label, Link, LockComponent } from '../../ui';
import { Hide, Show } from '../../ui/icon';
import { composeValidators } from '../../utils';
import { required } from '../../utils/validators';
import { AuthProps } from '..';

const isRequired = required(ERROR_PASSWORD_EMPTY);

const PasswordField = styled.div`
  & > input:focus + div,
  & > input:active + div,
  & > input:hover + div {
    path {
      stroke: var(--bb-icon-color--hover);
    }
  }
  position: relative;
`;

const PasswordFieldDisabled = styled.div`
  position: relative;
  & > input:focus + div,
  & > input:active + div,
  & > input:hover + div {
  }
`;

const ToggleVisibility = styled.button`
  position: absolute;
  right: 1px;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 43px;
  height: 43px;
  min-width: 45px;
  background-color: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: -21.5px 0 0 0;
  /* transform: translateY(-50%); */
  border-radius: 0 var(--bb-field-border-radius) var(--bb-field-border-radius) 0;
  & svg {
    stroke: var(--bb-icon-color);
  }
  &:hover {
    cursor: pointer;
    background-color: var(--bb-field-button-background--hover);
  }

  &:focus svg {
    stroke: var(--bb-icon-color--hover);
  }
`;

const ForgotContainer = styled.div`
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StayContainer = styled.label`
  display: flex;
  align-items: flex-start;
`;

const StayText = styled.span`
  color: var(--bb-field-color);

  &:hover {
    cursor: pointer;
  }
`;

const passwordBoldLink = css`
  font-weight: bold;
`;

const ChangeAccountText = styled.label`
  display: flex;
  align-items: flex-start;
  margin-top: 16px;
  color: #9e9e9e;
`;

type PasswordProps = { authenticated: boolean } & Pick<
  AuthProps,
  'forgotPasswordLabel' | 'forgotPasswordLink' | 'withIcon'
>;

const Password: React.FC<PasswordProps> = ({
  forgotPasswordLink,
  authenticated,
  forgotPasswordLabel,
  withIcon,
}) => {
  const [type, setType] = useState('password');
  const { input, meta } = useField('password', {
    validate: composeValidators(isRequired),
  });

  const { input: stayInput, meta: stayMeta } = useField('stay_signed_in', {
    type: 'checkbox',
  });
  const id = useId();
  return (
    <>
      {authenticated ? (
        <>
          <Label htmlFor={id}>Password</Label>
          <PasswordFieldDisabled>
            <Input
              id={id}
              type={type}
              disabled={authenticated}
              placeholder="You are currently logged in"
              {...input}
              style={{
                paddingLeft: withIcon ? '40px' : 'var(--bb-field-padding-left)',
              }}
              validate="submit"
              meta={meta}
            />
            <LockComponent meta={meta} />
            <FieldSubmitError meta={meta} />
          </PasswordFieldDisabled>
          <ChangeAccountText>
            If you wish to use a different account, please log out first.
          </ChangeAccountText>
        </>
      ) : (
        <>
          <Label htmlFor={id}>Password</Label>
          <PasswordField>
            <Input
              id={id}
              type={type}
              placeholder="Enter your password"
              validate="submit"
              {...input}
              meta={meta}
              style={{
                paddingLeft: withIcon ? '40px' : 'var(--bb-field-padding-left)',
              }}
            />
            {withIcon && <LockComponent meta={meta} />}
            <ToggleVisibility
              onClick={() => setType(type === 'password' ? 'text' : 'password')}
              type="button"
            >
              {type === 'password' && <Show />}
              {type === 'text' && <Hide />}
            </ToggleVisibility>
          </PasswordField>

          <FieldSubmitError meta={meta} />

          <ForgotContainer>
            <StayContainer>
              <Checkbox
                {...stayInput}
                meta={stayMeta}
              />
              <StayText>Stay logged in</StayText>
            </StayContainer>
            {forgotPasswordLink && (
              <Link
                href={forgotPasswordLink}
                className={passwordBoldLink}
              >
                {forgotPasswordLabel}
              </Link>
            )}
          </ForgotContainer>
        </>
      )}
    </>
  );
};

export default React.memo(Password);
